import { useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

// Optional, so validation only bites once something has been typed.
function isValidUrl(value: string): boolean {
  try {
    return new URL(value.trim()).protocol === 'https:'
  } catch {
    return false
  }
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (url: string) => Promise<void>
}

export function ScheduleLeadDialog({ open, onOpenChange, onSubmit }: Props) {
  const [url, setUrl] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const badUrl = url.trim().length > 0 && !isValidUrl(url)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (badUrl || submitting) return
    setSubmitting(true)
    try {
      await onSubmit(url)
      toast.success(
        fbt(
          "Thanks! We'll get the times in as soon as we can.",
          'Toast after successfully reporting that set times are published',
        ),
      )
      setUrl('')
      onOpenChange(false)
    } catch {
      // runMutation already surfaced the error toast.
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        onOpenChange(next)
        if (!next) setUrl('')
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <fbt desc="Dialog title for telling Beacon that an event's set times are published">
              Set times are out?
            </fbt>
          </DialogTitle>
          <DialogDescription>
            <fbt desc="Dialog subtitle explaining what happens after reporting set times are published">
              Let us know and we'll add them. A link helps, but it's fine to send this on its own
              if the times are only in the festival's app.
            </fbt>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Input
              autoFocus
              type="url"
              inputMode="url"
              data-testid="set-times-url"
              placeholder={fbt(
                'Link to the schedule (optional)',
                'Placeholder for the optional link field when reporting set times',
              ).toString()}
              value={url}
              onChange={e => setUrl(e.target.value)}
              aria-invalid={badUrl}
              maxLength={2000}
            />
            {badUrl && (
              <p className="text-sm text-destructive">
                <fbt desc="Validation error when the pasted schedule link is not a valid https URL">
                  That doesn't look like a valid link. It should start with https://
                </fbt>
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={submitting || badUrl}>
              {submitting
                ? fbt('Sending…', 'Button label while a set-times report is being submitted')
                : fbt('Send', 'Button label to submit the set-times report form')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
